import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, X, Loader2, Sparkles, Zap, Wind, Flame } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { useDraggable } from "@/hooks/useDraggable";
import { SorteoBroadcast } from "@/components/sorteo/SorteoBroadcast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POWERS = [
  { id: "slow",   label: "Suave",  icon: Wind,  color: "text-cyan-300",    bg: "border-cyan-400/40 bg-cyan-500/10",  active: "border-cyan-300 bg-cyan-500/30" },
  { id: "normal", label: "Normal", icon: Zap,   color: "text-yellow-300",  bg: "border-yellow-400/40 bg-yellow-500/10", active: "border-yellow-300 bg-yellow-500/30" },
  { id: "strong", label: "Fuerte", icon: Flame, color: "text-orange-400",  bg: "border-orange-500/40 bg-orange-500/10", active: "border-orange-300 bg-orange-500/30" },
];

export const SorteoFloat = () => {
  const { isUser, token } = useAuth();
  const [sorteo, setSorteo] = useState(null);
  const [open, setOpen] = useState(false);
  const [voting, setVoting] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const wheelRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [revealedWinner, setRevealedWinner] = useState(null);
  const [closeIn, setCloseIn] = useState(0);
  // Precompute spark trajectories so rendering stays pure
  const sparks = useMemo(
    () => Array.from({ length: 28 }).map((_, i) => {
      const colors = ["#d4af37", "#a855f7", "#22d3ee", "#f97316", "#ef4444", "#10b981", "#ec4899"];
      return {
        id: i,
        color: colors[i % colors.length],
        endX: (((i * 137) % 360) - 180),
        endY: 220 + ((i * 53) % 80),
        rot: (((i * 211) % 720) - 360),
        duration: 2.6 + ((i * 0.13) % 1.4),
        delay: (i * 0.011) % 0.3,
      };
    }),
    []
  );
  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  const drag = useDraggable();

  const load = useCallback(async () => {
    if (!isUser) { setSorteo(null); return; }
    try {
      const { data } = await axios.get(`${API}/sorteo/active`, auth);
      // Preserve in-flight celebration & don't unmount the dialog on a brief
      // null window (e.g. between sorteos or during spin completion).
      if (!data.sorteo) {
        if (revealedWinner || open) return;
      }
      setSorteo(data.sorteo || null);
    } catch {
      if (revealedWinner) return;
      // Don't clobber on transient errors — just leave state alone
    }
  }, [isUser, token, revealedWinner, open]);

  useEffect(() => { load(); }, [load]);

  // Preload Web Speech API voices (some browsers load async)
  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      window.speechSynthesis.getVoices();
      const handler = () => window.speechSynthesis.getVoices();
      window.speechSynthesis.addEventListener?.("voiceschanged", handler);
      return () => window.speechSynthesis.removeEventListener?.("voiceschanged", handler);
    } catch { /* noop */ }
  }, []);

  // Tick countdown every second (only restart when the sorteo identity/status changes)
  useEffect(() => {
    if (!sorteo || sorteo.status !== "voting") return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [sorteo?.id, sorteo?.status]);

  // SSE: react to sorteo updates (the hook passes the parsed event data directly)
  useRealtimeEvents({
    sorteo_update: (data) => {
      const kind = data?.kind;
      if (kind === "deleted") {
        setSorteo(null);
        setOpen(false);
        return;
      }
      if (kind === "started" || kind === "created") {
        // Auto-open the dialog so users immediately see the new sorteo
        setOpen(true);
        load();
        return;
      }
      if (kind === "spin") {
        setOpen(true);
        animateSpin(data);
        load();
        return;
      }
      if (kind === "vote") {
        // Apply live vote counts immediately — no refetch needed
        setSorteo((s) => {
          if (!s) return s;
          return {
            ...s,
            power_votes: data.power_votes || s.power_votes,
            voters_count: typeof data.voters_count === "number" ? data.voters_count : s.voters_count,
          };
        });
        return;
      }
      // Fallback (visibility, completed, etc.)
      load();
    },
  });

  const vote = async (power) => {
    if (!sorteo || sorteo.status !== "voting") return;
    setVoting(true);
    try {
      const { data } = await axios.post(`${API}/sorteo/${sorteo.id}/vote`, { power }, auth);
      // Apply server response immediately (SSE will reach other clients).
      setSorteo((s) => {
        if (!s) return s;
        return {
          ...s,
          my_vote: power,
          power_votes: data?.power_votes || s.power_votes,
          voters_count: typeof data?.voters_count === "number" ? data.voters_count : s.voters_count,
        };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error votando");
    } finally {
      setVoting(false);
    }
  };

  const speakWinner = (winnerName, prize) => {
    try {
      if (typeof window === "undefined" || !window.speechSynthesis) return;
      const text =
        `¡Felicidades ${winnerName}! Eres la persona ganadora del sorteo. ` +
        `Has ganado ${prize}. Que el universo te llene de bendiciones.`;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "es-MX";
      u.rate = 0.98;
      u.pitch = 1.02;
      u.volume = 1.0;
      // Pick the best Spanish voice if available
      const voices = window.speechSynthesis.getVoices() || [];
      const preferred =
        voices.find((v) => /es[-_]MX/i.test(v.lang)) ||
        voices.find((v) => /es[-_]US/i.test(v.lang)) ||
        voices.find((v) => v.lang?.toLowerCase().startsWith("es"));
      if (preferred) u.voice = preferred;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch { /* noop */ }
  };

  const animateSpin = (data) => {
    const seed = data?.spin_seed;
    const winner = data?.winner;
    if (!seed || !winner || !sorteo) return;
    const total = (sorteo.candidates || []).length;
    if (!total) return;
    const sliceDeg = 360 / total;
    const targetIdx = seed.target_index;
    // Final position should place the target slice at the top pointer.
    // Each slice center: (idx + 0.5) * sliceDeg from 0°. We want that to align with 270° (top).
    const finalAngle = 270 - (targetIdx + 0.5) * sliceDeg;
    const extraTurns = seed.extra_turns || 8;
    const dest = 360 * extraTurns + finalAngle;
    setRotation((r) => r + dest);
    setSpinning(true);
    setOpen(true);
    const duration = (seed.spin_duration_ms || 5000) + 200;
    setTimeout(() => {
      setSpinning(false);
      setRevealedWinner(winner);
      const prizeText = sorteo?.prize || "una sorpresa mística";
      toast.success(`🏆 ¡Ganador: ${winner.name}!`, { duration: 9000 });
      speakWinner(winner.name, prizeText);
      // Start the 20-second auto-close countdown
      setCloseIn(20);
    }, duration);
  };

  // Auto-close countdown ticker after winner is revealed
  useEffect(() => {
    if (!revealedWinner || closeIn <= 0) return;
    if (closeIn === 1) {
      const t = setTimeout(() => {
        setOpen(false);
        setRevealedWinner(null);
        setCloseIn(0);
        try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      }, 1000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCloseIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [revealedWinner, closeIn]);

  // When sorteo data first loads (or status changes to voting), auto-open the dialog
  // so users who join after the sorteo started ALSO see it immediately.
  const lastSeenSorteoIdRef = useRef(null);
  useEffect(() => {
    if (!sorteo) return;
    if (sorteo.status === "voting" && lastSeenSorteoIdRef.current !== sorteo.id) {
      lastSeenSorteoIdRef.current = sorteo.id;
      setOpen(true);
    }
  }, [sorteo?.id, sorteo?.status]);

  // Reset celebration state when a new voting/created sorteo arrives
  useEffect(() => {
    if (sorteo?.status === "voting") {
      setRevealedWinner(null);
      setCloseIn(0);
    }
  }, [sorteo?.id, sorteo?.status]);

  // If the user joins late and the sorteo is already spinning/completed, replay
  useEffect(() => {
    if (!sorteo) return;
    if ((sorteo.status === "spinning" || sorteo.status === "completed") && sorteo.spin_seed && sorteo.winner && !revealedWinner && !spinning) {
      animateSpin({ spin_seed: sorteo.spin_seed, winner: sorteo.winner });
    }
  }, [sorteo?.id, sorteo?.status]);

  if (!isUser || !sorteo) return null;

  const total = (sorteo.candidates || []).length;
  const sliceDeg = total ? 360 / total : 0;
  const ends = sorteo.voting_ends_at ? new Date(sorteo.voting_ends_at).getTime() : 0;
  const remaining = Math.max(0, Math.round((ends - now) / 1000));
  const votes = sorteo.power_votes || { slow: 0, normal: 0, strong: 0 };
  const totalVotes = (votes.slow || 0) + (votes.normal || 0) + (votes.strong || 0);

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="sorteo-float-btn"
        onClick={() => setOpen(true)}
        {...drag}
        className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-[#d4af37] via-yellow-500 to-orange-600 shadow-[0_0_30px_rgba(212,175,55,0.6)] flex items-center justify-center hover:scale-110 transition-all border-2 border-[#d4af37]/80"
        title="Sorteo en vivo"
      >
        <Trophy className="w-6 h-6 text-black" />
        {sorteo.status === "voting" && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold border border-red-300">
            {remaining}s
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-8"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-3xl border border-[#d4af37]/40 bg-gradient-to-br from-black via-[#0a0612] to-black p-6 sm:p-8"
              data-testid="sorteo-dialog"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400"
                data-testid="sorteo-close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="text-center mb-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Sorteo Místico</div>
                <h2 className="font-serif text-2xl text-white mt-1" data-testid="sorteo-title">{sorteo.title}</h2>
                <p className="text-xs text-neutral-400 italic mt-1">Premio: <span className="text-[#d4af37]">{sorteo.prize}</span></p>
              </div>

              {/* Wheel */}
              <div className="relative w-64 h-64 mx-auto mb-6">
                {/* pointer */}
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-10 w-0 h-0 border-l-[10px] border-r-[10px] border-b-[18px] border-l-transparent border-r-transparent border-b-[#d4af37]" />
                <div
                  ref={wheelRef}
                  className="w-full h-full rounded-full border-4 border-[#d4af37] shadow-[0_0_40px_rgba(212,175,55,0.6)] relative overflow-hidden"
                  style={{
                    background: total ? `conic-gradient(${(sorteo.candidates || [])
                      .map((c, i) => `${c.color} ${(i * sliceDeg)}deg ${(i + 1) * sliceDeg}deg`)
                      .join(",")})` : "#222",
                    transform: `rotate(${rotation}deg)`,
                    transition: spinning ? `transform ${sorteo.spin_seed?.spin_duration_ms || 5000}ms cubic-bezier(0.15, 0.9, 0.25, 1)` : "none",
                  }}
                  data-testid="sorteo-wheel"
                >
                  {(sorteo.candidates || []).map((c, i) => {
                    const angle = i * sliceDeg + sliceDeg / 2;
                    return (
                      <div
                        key={c.id}
                        className="absolute left-1/2 top-1/2 origin-left text-black text-[10px] font-bold uppercase tracking-wide whitespace-nowrap"
                        style={{
                          transform: `rotate(${angle}deg) translateX(40px)`,
                        }}
                      >
                        {c.name.slice(0, 14)}
                      </div>
                    );
                  })}
                </div>
                {/* center */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-12 h-12 rounded-full bg-black border-2 border-[#d4af37] flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-[#d4af37]" />
                  </div>
                </div>
              </div>

              {/* Live broadcast (Daily.co) — visible ONLY during the spin
                  itself. Hidden during the voting phase so the audio
                  transmission doesn't compete with the vote UI. */}
              {sorteo.status === "spinning" && sorteo.daily_room_url && (
                <div className="mb-4">
                  <SorteoBroadcast sorteoId={sorteo.id} token={token} isAdmin={false} />
                </div>
              )}

              {/* Status / countdown */}
              {sorteo.status === "voting" && (
                <>
                  <div className="text-center text-sm text-neutral-300 mb-3">
                    Vota el <span className="text-[#d4af37]">poder del giro</span>
                    <span className="ml-2 text-[#d4af37] font-bold" data-testid="sorteo-countdown">⏱ {remaining}s</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {POWERS.map((p) => {
                      const Icon = p.icon;
                      const count = votes[p.id] || 0;
                      const pct = totalVotes ? Math.round((count * 100) / totalVotes) : 0;
                      const isMine = sorteo.my_vote === p.id;
                      return (
                        <button
                          key={p.id}
                          data-testid={`sorteo-power-${p.id}`}
                          disabled={voting}
                          onClick={(e) => { e.stopPropagation(); vote(p.id); }}
                          onTouchEnd={(e) => { e.stopPropagation(); }}
                          className={`p-3 rounded-xl border transition-all ${isMine ? p.active : p.bg} hover:scale-105 disabled:opacity-50`}
                        >
                          <Icon className={`w-5 h-5 mx-auto mb-1 ${p.color}`} />
                          <div className="text-[10px] uppercase tracking-widest text-white">{p.label}</div>
                          <div className="text-xs text-neutral-300 mt-1">{count} · {pct}%</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-center text-neutral-500">
                    Tu voto: {sorteo.my_vote
                      ? <span className="text-[#d4af37] uppercase font-semibold">{POWERS.find(p => p.id === sorteo.my_vote)?.label}</span>
                      : "todavía no votas"}
                  </div>
                </>
              )}

              {sorteo.status === "spinning" && !revealedWinner && (
                <div className="text-center text-[#d4af37] flex items-center justify-center gap-2" data-testid="sorteo-spinning">
                  <Loader2 className="w-4 h-4 animate-spin" /> Girando con poder {sorteo.spin_speed?.toUpperCase()}...
                </div>
              )}

              {/* Animated winner celebration */}
              {revealedWinner && (
                <motion.div
                  data-testid="sorteo-celebration"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="relative text-center mt-2"
                >
                  {/* Confetti sparks */}
                  <div className="pointer-events-none absolute inset-0 -top-10 overflow-visible">
                    {sparks.map((s) => (
                      <motion.span
                        key={s.id}
                        initial={{ x: 0, y: -10, opacity: 1, rotate: 0 }}
                        animate={{ x: s.endX, y: s.endY, opacity: 0, rotate: s.rot }}
                        transition={{ duration: s.duration, ease: "easeOut", delay: s.delay }}
                        className="absolute left-1/2 top-0 w-2 h-3 rounded-sm"
                        style={{ backgroundColor: s.color }}
                      />
                    ))}
                  </div>

                  <motion.div
                    animate={{ rotate: [0, -6, 6, -4, 4, 0] }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                    className="text-5xl mb-2"
                  >
                    🏆
                  </motion.div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">¡Felicidades!</div>
                  <motion.div
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="font-serif text-3xl text-white mt-1 mb-1"
                  >
                    {revealedWinner.name}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-sm text-neutral-300"
                  >
                    Ha ganado: <span className="text-[#d4af37] font-semibold">{sorteo.prize}</span>
                  </motion.div>
                  <div className="mt-4 text-[11px] text-neutral-500">
                    Cerrando en <span className="text-[#d4af37] font-semibold" data-testid="sorteo-autoclose">{closeIn}s</span>
                  </div>
                </motion.div>
              )}

              {/* Fallback display when sorteo is completed but no celebration is showing (e.g., late join) */}
              {sorteo.status === "completed" && sorteo.winner && !revealedWinner && (
                <div className="text-center" data-testid="sorteo-winner">
                  <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-1">🏆 Ganador</div>
                  <div className="font-serif text-2xl text-white">{sorteo.winner.name}</div>
                  <div className="text-sm text-neutral-400 mt-1">Premio: <span className="text-[#d4af37]">{sorteo.prize}</span></div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
